/**
 * Google Consent Mode v2 Implementation
 * Privacy-first consent management with regional compliance support
 * Supports GDPR, ePrivacy, CCPA, and other privacy regulations
 */

/**
 * Consent Mode v2 Types and Constants
 */
export const CONSENT_TYPES = {
  AD_STORAGE: 'ad_storage',
  ANALYTICS_STORAGE: 'analytics_storage',
  AD_USER_DATA: 'ad_user_data',
  AD_PERSONALIZATION: 'ad_personalization'
};

export const CONSENT_VALUES = {
  GRANTED: 'granted',
  DENIED: 'denied'
};

export const REGIONS = {
  EU: 'EU',      // European Union (GDPR)
  UK: 'GB',      // United Kingdom
  CALIFORNIA: 'US-CA', // California (CCPA)
  GLOBAL: 'GLOBAL'
};

/**
 * Default consent configuration based on regional requirements
 */
const DEFAULT_CONSENT_CONFIG = {
  [REGIONS.EU]: {
    [CONSENT_TYPES.AD_STORAGE]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.ANALYTICS_STORAGE]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_USER_DATA]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_PERSONALIZATION]: CONSENT_VALUES.DENIED
  },
  [REGIONS.UK]: {
    [CONSENT_TYPES.AD_STORAGE]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.ANALYTICS_STORAGE]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_USER_DATA]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_PERSONALIZATION]: CONSENT_VALUES.DENIED
  },
  [REGIONS.CALIFORNIA]: {
    [CONSENT_TYPES.AD_STORAGE]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.ANALYTICS_STORAGE]: CONSENT_VALUES.GRANTED,
    [CONSENT_TYPES.AD_USER_DATA]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_PERSONALIZATION]: CONSENT_VALUES.DENIED
  },
  [REGIONS.GLOBAL]: {
    [CONSENT_TYPES.AD_STORAGE]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.ANALYTICS_STORAGE]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_USER_DATA]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_PERSONALIZATION]: CONSENT_VALUES.DENIED
  }
};

/**
 * Storage keys for consent preferences
 */
const STORAGE_KEYS = {
  CONSENT_PREFERENCES: 'tcl_consent_preferences',
  CONSENT_TIMESTAMP: 'tcl_consent_timestamp',
  CONSENT_VERSION: 'tcl_consent_version',
  USER_REGION: 'tcl_user_region'
};

const CONSENT_VERSION = '1.0';
const CONSENT_EXPIRY_DAYS = 365; // 1 year

/**
 * Detect user's region for compliance requirements
 */
export const detectUserRegion = () => {
  try {
    // Try to detect based on timezone and locale
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const locale = navigator.language || navigator.languages[0];
    
    // EU detection (simplified - in production use a proper geolocation service)
    const euTimezones = [
      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
      'Europe/Rome', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna',
      'Europe/Stockholm', 'Europe/Helsinki', 'Europe/Oslo', 'Europe/Dublin',
      'Europe/Prague', 'Europe/Warsaw', 'Europe/Budapest', 'Europe/Bucharest'
    ];
    
    if (euTimezones.some(tz => timezone.includes(tz))) {
      return locale.startsWith('en-GB') ? REGIONS.UK : REGIONS.EU;
    }
    
    // California detection
    if (timezone.includes('America/Los_Angeles') || locale.includes('US')) {
      return REGIONS.CALIFORNIA;
    }
    
    return REGIONS.GLOBAL;
  } catch (error) {
    console.warn('Region detection failed, defaulting to GLOBAL:', error);
    return REGIONS.GLOBAL;
  }
};

/**
 * Get stored user region or detect it
 */
export const getUserRegion = () => {
  try {
    const storedRegion = localStorage.getItem(STORAGE_KEYS.USER_REGION);
    if (storedRegion && Object.values(REGIONS).includes(storedRegion)) {
      return storedRegion;
    }
  } catch (error) {
    console.warn('Could not access localStorage for region:', error);
  }
  
  const detectedRegion = detectUserRegion();
  
  try {
    localStorage.setItem(STORAGE_KEYS.USER_REGION, detectedRegion);
  } catch (error) {
    console.warn('Could not store user region:', error);
  }
  
  return detectedRegion;
};

/**
 * Initialize Google Consent Mode with default values
 * Must be called before GTM loads
 */
export const initializeConsentMode = () => {
  if (typeof window === 'undefined') return;
  
  // Initialize gtag if not already available
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
  }
  
  const userRegion = getUserRegion();
  const defaultConsent = DEFAULT_CONSENT_CONFIG[userRegion];
  
  // Set default consent state
  window.gtag('consent', 'default', {
    ...defaultConsent,
    region: [userRegion],
    wait_for_update: 500 // Wait 500ms for user consent update
  });
  
  console.log('Consent Mode v2 initialized with default state:', {
    region: userRegion,
    consent: defaultConsent
  });
};

/**
 * Update consent based on user choices
 */
export const updateConsent = (consentChoices) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('gtag not available for consent update');
    return;
  }
  
  const timestamp = new Date().toISOString();
  
  // Update consent mode
  window.gtag('consent', 'update', consentChoices);
  
  // Store user preferences
  try {
    localStorage.setItem(STORAGE_KEYS.CONSENT_PREFERENCES, JSON.stringify(consentChoices));
    localStorage.setItem(STORAGE_KEYS.CONSENT_TIMESTAMP, timestamp);
    localStorage.setItem(STORAGE_KEYS.CONSENT_VERSION, CONSENT_VERSION);
  } catch (error) {
    console.warn('Could not store consent preferences:', error);
  }
  
  console.log('Consent updated:', {
    choices: consentChoices,
    timestamp
  });
  
  // Trigger custom event for analytics
  window.gtag('event', 'consent_update', {
    event_category: 'privacy',
    consent_ad_storage: consentChoices[CONSENT_TYPES.AD_STORAGE],
    consent_analytics_storage: consentChoices[CONSENT_TYPES.ANALYTICS_STORAGE],
    consent_ad_user_data: consentChoices[CONSENT_TYPES.AD_USER_DATA],
    consent_ad_personalization: consentChoices[CONSENT_TYPES.AD_PERSONALIZATION],
    consent_timestamp: timestamp
  });
};

/**
 * Get current consent preferences
 */
export const getConsentPreferences = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CONSENT_PREFERENCES);
    if (stored) {
      const preferences = JSON.parse(stored);
      const timestamp = localStorage.getItem(STORAGE_KEYS.CONSENT_TIMESTAMP);
      const version = localStorage.getItem(STORAGE_KEYS.CONSENT_VERSION);
      
      // Check if consent is still valid (not expired)
      if (timestamp && version === CONSENT_VERSION) {
        const consentDate = new Date(timestamp);
        const expiryDate = new Date(consentDate.getTime() + (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000));
        
        if (new Date() < expiryDate) {
          return {
            preferences,
            timestamp,
            version,
            isValid: true
          };
        }
      }
    }
  } catch (error) {
    console.warn('Could not retrieve consent preferences:', error);
  }
  
  return {
    preferences: null,
    timestamp: null,
    version: null,
    isValid: false
  };
};

/**
 * Check if user has made consent choices
 */
export const hasUserConsented = () => {
  const { isValid } = getConsentPreferences();
  return isValid;
};

/**
 * Get default consent for user's region
 */
export const getDefaultConsent = () => {
  const userRegion = getUserRegion();
  return DEFAULT_CONSENT_CONFIG[userRegion];
};

/**
 * Reset consent preferences (for testing or user request)
 */
export const resetConsent = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.CONSENT_PREFERENCES);
    localStorage.removeItem(STORAGE_KEYS.CONSENT_TIMESTAMP);
    localStorage.removeItem(STORAGE_KEYS.CONSENT_VERSION);
    
    console.log('Consent preferences reset');
    
    // Reinitialize with defaults
    initializeConsentMode();
  } catch (error) {
    console.warn('Could not reset consent preferences:', error);
  }
};

/**
 * Apply stored consent preferences on page load
 */
export const applyStoredConsent = () => {
  const { preferences, isValid } = getConsentPreferences();
  
  if (isValid && preferences) {
    updateConsent(preferences);
    return true;
  }
  
  return false;
};

/**
 * Consent management class for easier integration
 */
export class ConsentManager {
  constructor() {
    this.callbacks = [];
    this.initialized = false;
  }
  
  /**
   * Initialize consent management
   */
  init() {
    if (this.initialized) return;
    
    initializeConsentMode();
    
    // Apply stored consent if available
    const hasStored = applyStoredConsent();
    
    this.initialized = true;
    
    return hasStored;
  }
  
  /**
   * Update consent and notify callbacks
   */
  updateConsent(choices) {
    updateConsent(choices);
    this.notifyCallbacks(choices);
  }
  
  /**
   * Add callback for consent changes
   */
  onConsentChange(callback) {
    this.callbacks.push(callback);
  }
  
  /**
   * Remove callback
   */
  removeCallback(callback) {
    this.callbacks = this.callbacks.filter(cb => cb !== callback);
  }
  
  /**
   * Notify all callbacks of consent changes
   */
  notifyCallbacks(choices) {
    this.callbacks.forEach(callback => {
      try {
        callback(choices);
      } catch (error) {
        console.error('Consent callback error:', error);
      }
    });
  }
  
  /**
   * Get current consent status
   */
  getConsentStatus() {
    return getConsentPreferences();
  }
  
  /**
   * Check if consent is required for user's region
   */
  isConsentRequired() {
    const region = getUserRegion();
    return region === REGIONS.EU || region === REGIONS.UK;
  }
  
  /**
   * Get user's region
   */
  getUserRegion() {
    return getUserRegion();
  }
}

// Create singleton instance
export const consentManager = new ConsentManager();

const consentModeExports = {
  CONSENT_TYPES,
  CONSENT_VALUES,
  REGIONS,
  initializeConsentMode,
  updateConsent,
  getConsentPreferences,
  hasUserConsented,
  getDefaultConsent,
  resetConsent,
  applyStoredConsent,
  getUserRegion,
  ConsentManager,
  consentManager
};

export default consentModeExports;